import { useState, useEffect, useCallback } from 'react'
import supabase from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

/**
 * Generic hook for CRUD on a Supabase table scoped to an empresa_id.
 *
 * El rol se lee siempre del contexto de auth (no de un prop) para que
 * ningún llamador pueda saltarse el aislamiento por tenant por accidente.
 * Excepción única: rol === 'superadmin' ve todas las empresas.
 *
 * @param {string} tableName  - Supabase table name
 * @param {object} opts
 * @param {string} opts.empresaId  - filter rows by empresa_id
 * @param {Array}  opts.filters    - extra filters [{ column, value }]
 * @param {string} opts.orderBy    - column to order by (default 'created_at')
 * @param {boolean} opts.ascending - sort order (default true)
 */
export function useSupabaseTable(tableName, { empresaId, filters = [], orderBy = 'created_at', ascending = true } = {}) {
  const { rol } = useAuth()
  const isSuperadmin = rol === 'superadmin'

  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const fetchData = useCallback(async () => {
    if (!tableName) return   // guard aquí evita el warning de eslint en useEffect

    // Nunca se ejecuta un SELECT sin filtro de empresa_id salvo superadmin.
    // Sin empresaId y sin ser superadmin: no hay consulta, array vacío.
    if (!isSuperadmin && !empresaId) {
      setRows([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    let query = supabase.from(tableName).select('*')

    if (!isSuperadmin) query = query.eq('empresa_id', empresaId)
    filters.forEach(f => { query = query.eq(f.column, f.value) })
    query = query.order(orderBy, { ascending })

    const { data, error: err } = await query

    if (err) setError(err)
    else setRows(data || [])

    setLoading(false)
  }, [tableName, empresaId, isSuperadmin, orderBy, ascending, JSON.stringify(filters)])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ── Update a single row ────────────────────────────────────────
  const updateRow = async (id, updates) => {
    let query = supabase.from(tableName).update(updates).eq('id', id)
    // Defensa en profundidad: además de RLS, el cliente nunca manda
    // un UPDATE fuera de su propia empresa (salvo superadmin).
    if (!isSuperadmin) query = query.eq('empresa_id', empresaId)

    const { error: err } = await query

    // Optimistic update — avoids the .select().single() PGRST116 RLS trap
    if (!err) {
      setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r))
    }
    return { data: null, error: err }
  }

  // ── Insert a new row ───────────────────────────────────────────
  const addRow = async (newRow) => {
    const targetEmpresaId = isSuperadmin && newRow?.empresa_id ? newRow.empresa_id : empresaId
    if (!targetEmpresaId) {
      return { data: null, error: { message: 'empresa_id es null — usuario sin empresa activa. Cierra sesión y vuelve a entrar.' } }
    }
    const { empresa_id: _drop, ...rest } = newRow || {}
    const payload = { ...rest, empresa_id: targetEmpresaId }

    // No .select().single() — that combo fails with PGRST116 when RLS allows
    // INSERT but blocks the implicit RETURNING SELECT. Plain insert + refetch
    // is resilient to any RLS SELECT policy configuration.
    const { error: err } = await supabase
      .from(tableName)
      .insert(payload)

    if (!err) await fetchData()
    return { data: null, error: err }
  }

  // ── Delete a row ───────────────────────────────────────────────
  const deleteRow = async (id) => {
    let query = supabase.from(tableName).delete().eq('id', id)
    if (!isSuperadmin) query = query.eq('empresa_id', empresaId)

    const { error: err } = await query

    if (!err) setRows(prev => prev.filter(r => r.id !== id))
    return { error: err }
  }

  return {
    rows,
    loading,
    error,
    updateRow,
    addRow,
    deleteRow,
    refetch: fetchData,
  }
}
